import { LOGIN_REDIRECT_PARAMETER } from '../components/Login/DusktechLogin';
import { safeLocalRedirect } from '../utils/url';

declare const tag: unique symbol;

// opaque type to prevent using string literals as routes
export type Route = string & { readonly [tag]: 'route' };

export type RedirectOptions = { redirect?: Route };

export function raw(url: string) {
  return () => url as Route;
}

/**
 * Create a route mapping that accepts a redirect option
 * @param from
 */
function withRedirect(from: string) {
  return (opts?: RedirectOptions): Route => {
    if (!opts?.redirect) {
      return from as Route;
    }

    const redirectLocation = safeLocalRedirect(opts.redirect);
    if (!redirectLocation) {
      throw Error(`${opts.redirect} is not a valid redirect location`);
    }
    return `${from}?${LOGIN_REDIRECT_PARAMETER}=${redirectLocation}` as Route;
  };
}

export const routes = {
  home: raw('/'),
  roadmap: raw('/roadmap'),
  team: raw('/team'),
  comic: {
    base: raw('/comic'),
    single: (opts: SingleRoute) => `/comic/${opts.id}/${opts.slug}` as Route,
  },
  news: {
    base: raw('/news'),
    single: (opts: SingleRoute) => `/news/${opts.id}/${opts.slug}` as Route,
  },
  lore: {
    base: raw('/lore'),
    single: (opts: SingleRoute) => `/lore/${opts.id}/${opts.slug}` as Route,
  },
  gallery: {
    base: raw('/gallery'),
    duskbreakers: raw('/gallery/duskbreakers'),
    eggs: raw('/gallery/gribble-eggs'),
  },
  faq: raw('/faq'),
  terms: raw('/terms'),
  media: raw('/media'),
  provenance: raw('/provenance'),
  leaderboards: {
    base: raw('/leaderboards'),
    singleLeaderboard: (opts: { type: string }) => `/leaderboards/${opts.type}` as Route,
  },
  help: raw('/help'),
  game: raw('/game'),
  interactiveStories: {
    base: raw('/interactive-stories'),
    dialog: (opts: { slug: string }) => `/interactive-stories/${opts.slug}` as Route,
  },
  nftTerms: raw('/nft-terms'),
  register: raw('/register'),
  login: withRedirect('/login'),
  loginDusktech: withRedirect('/login/dusktech-id'),
  loginMetamask: withRedirect('/login/metamask'),
  forgotPassword: raw('/forgot-password'),
  resetPassword: raw('/reset-password'),
  launcher: raw('/launcher'),
  adrian: raw('/adrian'),
  adrianSanctuary: raw('/adrian-sanctuary'),
  mint: raw('/mint'),
  play: raw('/play'),
  dashboard: raw('/eggs'),
  stories: raw('/stories'),
  missions: raw('/missions'),
  referral: raw('/refer-a-friend'),
  leaderboard: {
    base: raw('/contest'),
    page: (opts: { type: string }) => `/contest/${opts.type}` as Route,
  },
  collection: {
    base: raw('/your-collection'),
    page: (opts: { filter: string }) => `/your-collection/${opts.filter}` as Route,
  },
  egg: {
    //leave old leaderboard routes and "Navigate replace" them to new path to help SEO
    mint: raw('/egg/mint'),
    play: raw('/egg/play'),
    leaderboard: {
      base: raw('/egg/contest'),
      page: (opts: { type: string }) => `/egg/contest/${opts.type}` as Route,
    },
  },

  playToMint: raw('/play-to-mint'),
  duskAccount: {
    base: raw('/dusk-account'),
    eggCollection: raw('/dusk-account/egg-collection'),
    badgeCollection: raw('/dusk-account/badge-collection'),
    gribbleCollection: raw('/dusk-account/gribble-collection'),
  },
  hatchening: raw('/hatchening'),
  404: raw('/404'),
} as const;

export type SingleRoute = {
  id: string;
  slug: string;
};

declare module 'react-router-dom' {
  /**
   * overriding the useNavigate function to make sure we're forced
   * to call `navigate(routes.home())` instead of `navigate("/")`
   * to make sure we don't hardcode routes
   */
  type NavigateFunction = (typedRoute: Route) => void;

  function useNavigate(): NavigateFunction;
}
